import React, { useState, useEffect } from 'react';
import { FormError, SubmitStatus, SubmitStatusEnum } from '@typings/common';
import { ApiResponse } from '@typings/ApiResponse';
import {
  updateErrors,
  getErrorMessage as getErrorMessageFromErrors,
} from '@utils/commons';
import getErrorMessage from '@utils/errorUtils';
import {
  validatePassword,
  checkMatchPass,
  validateConfirmPassword,
} from '@pages/verify-account/utils';
import NewErrorSummary from '@components/NewErrorSummary';
import { useNavigate } from 'react-router-dom';
import { changePassword } from '@api/auth';

interface PasswordDetails {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const ChangingYourPassword: React.FC = () => {
  const [password, setPassword] = useState<PasswordDetails>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState<{
    currentPassword: boolean;
    newPassword: boolean;
  }>({
    currentPassword: false,
    newPassword: false,
  });
  const [changePassErrorMessage, setChangePassErrorMessage] =
    useState<PasswordDetails>({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });

  const [changePasswordStatus, setChangePasswordStatus] =
    useState<SubmitStatus>({
      loading: false,
      submitted: SubmitStatusEnum.IDLE,
      message: '',
    });
  const [errors, setErrors] = useState<FormError[]>([]);
  const navigate = useNavigate();

  const onSubmit = async (e: any) => {
    e.preventDefault();

    let validationErrors: FormError[] = [];

    validationErrors = updateErrors(
      validationErrors,
      'currentPassword',
      !password.currentPassword ? 'Please enter your current password' : ''
    );

    validationErrors = updateErrors(
      validationErrors,
      'newPassword',
      validatePassword(password.newPassword)
    );
    validationErrors = updateErrors(
      validationErrors,
      'confirmPassword',
      validateConfirmPassword(password.confirmPassword)
    );

    if (!validatePassword(password.newPassword) && password.confirmPassword) {
      validationErrors = updateErrors(
        validationErrors,
        'confirmPassword',
        checkMatchPass(password.newPassword, password.confirmPassword)
      );
    }
    setErrors(validationErrors);

    if (validationErrors.length !== 0) {
      return;
    }

    setChangePasswordStatus(prev => ({ ...prev, loading: true }));
    try {
      const res: ApiResponse<null> = await changePassword(
        password.currentPassword,
        password.newPassword
      );

      if (res?.status === 'success') {
        setChangePasswordStatus(prev => ({ ...prev, loading: false }));
        localStorage.removeItem('authToken');
        navigate('/password-success');
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setChangePasswordStatus({
        loading: false,
        submitted: SubmitStatusEnum.FAILED,
        message: errorMessage,
      });

      validationErrors = updateErrors(
        validationErrors,
        'changePassword',
        errorMessage
      );
      setErrors(validationErrors);
    } finally {
      setChangePasswordStatus(prev => ({ ...prev, loading: false }));
    }
  };

  const onResetForm = (e: any) => {
    e.preventDefault();
    setPassword({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
  };

  useEffect(() => {
    setChangePassErrorMessage({
      currentPassword:
        getErrorMessageFromErrors(errors, 'currentPassword') ?? '',
      newPassword: getErrorMessageFromErrors(errors, 'newPassword') ?? '',
      confirmPassword:
        getErrorMessageFromErrors(errors, 'confirmPassword') ?? '',
    });
  }, [errors]);

  return (
    <div>
      <h1 className="govuk-heading-l">Changing your password</h1>
      <p className="govuk-body">
        You can change your password by entering your email address and
        following the steps provided. These will be sent to you by email.
      </p>
      <p className="govuk-body">Your new password should be:</p>
      <ul className="govuk-list govuk-list--bullet">
        <li>at least 8 characters</li>
        <li>at least one lower-case letter</li>
        <li>at least one capital letters</li>
        <li>at least one number</li>
      </ul>

      <form>
        {errors.length > 0 && <NewErrorSummary errors={errors} />}
        <div
          className={`govuk-form-group ${changePassErrorMessage.currentPassword ? 'govuk-form-group--error' : ''}`}
        >
          <label className="govuk-label" htmlFor="currentPassword">
            Current password
          </label>
          {changePassErrorMessage.currentPassword && (
            <span id="password-error" className="govuk-error-message">
              <span className="govuk-visually-hidden">Error:</span>{' '}
              {changePassErrorMessage.currentPassword}
            </span>
          )}
          <input
            className={`govuk-input govuk-!-width-one-half ${changePassErrorMessage.currentPassword ? 'govuk-input--error' : ''}`}
            id="currentPassword"
            name="currentPassword"
            type={showPassword.currentPassword ? 'text' : 'password'}
            value={password.currentPassword}
            onChange={e =>
              setPassword(prev => ({
                ...prev,
                currentPassword: e.target.value,
              }))
            }
            aria-describedby={
              changePassErrorMessage.currentPassword
                ? 'current-password-error'
                : undefined
            }
            placeholder="Enter current password"
          />
          <div
            onClick={e => {
              e.preventDefault();
              setShowPassword(prev => ({
                ...prev,
                currentPassword: !prev.currentPassword,
              }));
            }}
            className="mt-1.5"
          >
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a href="#" className="govuk-link govuk-link--no-visited-state">
              {showPassword.currentPassword ? 'Hide password' : 'Show password'}
            </a>
          </div>
        </div>
        <div
          className={`govuk-form-group ${changePassErrorMessage.newPassword ? 'govuk-form-group--error' : ''}`}
        >
          <label className="govuk-label" htmlFor="newPassword">
            New password
          </label>
          {changePassErrorMessage.newPassword && (
            <span id="password-error" className="govuk-error-message">
              <span className="govuk-visually-hidden">Error:</span>{' '}
              {changePassErrorMessage.newPassword}
            </span>
          )}
          <input
            className={`govuk-input govuk-!-width-one-half ${changePassErrorMessage.newPassword ? 'govuk-input--error' : ''}`}
            id="newPassword"
            name="newPassword"
            type={showPassword.newPassword ? 'text' : 'password'}
            value={password.newPassword}
            onChange={e =>
              setPassword(prev => ({
                ...prev,
                newPassword: e.target.value,
              }))
            }
            aria-describedby={
              changePassErrorMessage.newPassword ? 'password-error' : undefined
            }
            placeholder="Enter new password"
          />
          <div
            onClick={e => {
              e.preventDefault();
              setShowPassword(prev => ({
                ...prev,
                newPassword: !prev.newPassword,
              }));
            }}
            className="mt-1.5"
          >
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a href="#" className="govuk-link govuk-link--no-visited-state">
              {showPassword.newPassword ? 'Hide password' : 'Show password'}
            </a>
          </div>
        </div>

        <div
          className={`govuk-form-group ${changePassErrorMessage.confirmPassword ? 'govuk-form-group--error' : ''}`}
        >
          <label className="govuk-label" htmlFor="confirmPassword">
            Confirm new password
          </label>
          {changePassErrorMessage.confirmPassword && (
            <span id="verifyPass-error" className="govuk-error-message">
              <span className="govuk-visually-hidden">Error:</span>{' '}
              {changePassErrorMessage.confirmPassword}
            </span>
          )}
          <input
            className={`govuk-input govuk-!-width-one-half ${changePassErrorMessage.confirmPassword ? 'govuk-input--error' : ''}`}
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            value={password.confirmPassword}
            onChange={e =>
              setPassword(prev => ({
                ...prev,
                confirmPassword: e.target.value,
              }))
            }
            aria-describedby={
              changePassErrorMessage.confirmPassword
                ? 'verifyPass-error'
                : undefined
            }
            placeholder="Reenter new password"
          />
        </div>

        <div className="govuk-button-group govuk-!-margin-0">
          <button
            type="submit"
            className="govuk-button"
            data-module="govuk-button"
            disabled={changePasswordStatus.loading}
            onClick={onSubmit}
          >
            Save new password
          </button>
          <button
            type="submit"
            className="govuk-button govuk-button--secondary"
            data-module="govuk-button"
            onClick={e => onResetForm(e)}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChangingYourPassword;
